import { IRule } from '@/classes/rules/IRule';
import { ObjectRule } from '@/classes/rules/ObjectRule';
import { MinMaxRule } from '@/classes/rules/MinMaxRule';
import { TextRule } from '@/classes/rules/TextRule';

import { OptionsRule } from '@/classes/rules/OptionsRule';
import { ScenarioGeneratorExt } from '@/classes/scenario/genset/ScenarioGeneratorExt';
import { BooleanRule } from '@/classes/rules/BooleanRule';
import { GensetExt } from '@/classes/scenario/genset/GensetExt';
import { GeneratorTechnology, GeneratorTypeRef } from '@/dto/scenario/generator/GeneratorReference';
import { DefaultValue } from '@/classes/rules/DefaultValue';
import { normalizeFloat } from '@/helpers/mathHelper';
import _env from '@/helpers/envHelper';
import { RuleFieldMessage } from '@/classes/rules/RuleFieldMessage';
import { ScenarioExt } from '@/classes/scenario/ScenarioExt';
import { EnumRule } from '@/classes/rules/EnumRule';
import { FieldUsage } from '@/classes/rules/FieldUsage';
import { SizingAlgorithmRef } from '@/dto/scenario/sizing/SizingReference';

// RULES CONST =========================================================

// HOURS OPTIONS
const fromHoursOptions: number[] = [];
const toHoursOptions: number[] = [];
for (let i = 0; i < 24; i++) {
  fromHoursOptions.push(i);
  toHoursOptions.push(i);
}
toHoursOptions.push(24);

function getOneGeneratorRules(genset: string): IRule[] {
  return [
    new BooleanRule('srPriority')
      .withIsAvailable(isMultigenset),

    new MinMaxRule('size', 0, 100000, 'kva'),

    new MinMaxRule('sizeKW', 0, 100000, 'kwh')
      .withIsAvailable(isMultigenset)
      .withDefaultValue(scenario => {
        if (!(scenario && scenario.generator && (scenario.generator as any)[genset] && (scenario.generator as any)[genset].size !== null)) {
          return null;
        }
        const sizeKVA = (scenario.generator as any)[genset].size;
        const defaultSizeKW = normalizeFloat((sizeKVA || 0) * 0.8, 3);
        return new DefaultValue(defaultSizeKW).withLock();
      }),

    new EnumRule('typeRef', GeneratorTypeRef)
      .withIsAvailable(scenario => (scenario && scenario.generator && !isMultigenset(scenario)) || false),

    new OptionsRule<number>('fromHour', fromHoursOptions)
      .withDefaultValue(() => new DefaultValue(0))
      .withIsAvailable(scenario => !!scenario.hasGenset),

    new OptionsRule<number>('toHour', toHoursOptions)
      .withDefaultValue(() => new DefaultValue(24))
      .withIsAvailable(scenario => !!scenario.hasGenset),

    new MinMaxRule('numberOfSimultaneous', 0)
      .withIsAvailable(isMultigenset)
      .withDefaultValue(() => new DefaultValue(1).withLock()),

    new MinMaxRule('numberOfSpares', 0)
      .withDefaultValue(() => new DefaultValue(0).withLock()),

    new MinMaxRule('minimumGeneratorLoad', 0, 1, '%')
      .withFactor(100)
      .withDefaultValue(() => new DefaultValue(0.3).withLock()),

    new MinMaxRule('quantity', 0)
      .withIsAvailable(isMultigenset)
      .withDefaultValue(() => new DefaultValue(1).withLock()),

    new MinMaxRule('fuelConsumptionAt25Percent', 0, undefined, 'l'),

    new MinMaxRule('fuelConsumptionAt50Percent', 0, undefined, 'l'),

    new MinMaxRule('fuelConsumptionAt75Percent', 0, undefined, 'l'),

    new MinMaxRule('fuelConsumptionAt100Percent', 0, undefined, 'l'),

    // multi
    new MinMaxRule('fuelConsumptionKWhAt25Percent', 0, undefined, 'l/h')
      .withIsAvailable(isMultigenset)
      .withRequired(false),

    new MinMaxRule('fuelConsumptionKWhAt50Percent', 0, undefined, 'l/h')
      .withIsAvailable(isMultigenset),

    new MinMaxRule('fuelConsumptionKWhAt75Percent', 0, undefined, 'l/h')
      .withIsAvailable(isMultigenset),

    new MinMaxRule('fuelConsumptionKWhAt100Percent', 0, undefined, 'l/h')
      .withIsAvailable(isMultigenset),

    new MinMaxRule('efficiencyAt25Percent', 0, 1, '%')
      .withIsAvailable(isMultigenset)
      .withFactor(100)
      .withRequired(false),

    new MinMaxRule('efficiencyAt50Percent', 0, 1, '%')
      .withIsAvailable(isMultigenset)
      .withFactor(100),

    new MinMaxRule('efficiencyAt75Percent', 0, 1, '%')
      .withIsAvailable(isMultigenset)
      .withFactor(100),

    new MinMaxRule('efficiencyAt100Percent', 0, 1, '%')
      .withIsAvailable(isMultigenset)
      .withFactor(100),

    new MinMaxRule('pci', 0, 10000, 'kwh/l')
      .withIsAvailable(isMultigenset),

    new TextRule('brand', /.*/s)
      .withIsAvailable(isMultigenset),

    new TextRule('reference', /.*/s)
      .withIsAvailable(isMultigenset),

    new EnumRule('technology', GeneratorTechnology)
      .withIsAvailable(isMultigenset)
      .withDefaultValue(() => new DefaultValue(GeneratorTechnology.diesel)),

    new MinMaxRule('addOneThreshold', 0, 1, '%')
      .withIsAvailable(isMultigenset)
      .withFactor(100),

    new MinMaxRule('rampUpTime', 0)
      .withIsAvailable(isMultigenset),

    new MinMaxRule('removeOneThreshold', 0, 1, '%')
      .withIsAvailable(isMultigenset)
      .withFactor(100),

    new MinMaxRule('lifetimeHours', 0, 100000, 'hour')
  ];
}

function isMultigenset(scenario: ScenarioExt): boolean {
  return scenario.isMultigenset();
}

// GENSET RULES =========================================================
export const buildGensetRules = () => new ObjectRule<ScenarioGeneratorExt>('generator', [
  new BooleanRule('isGeneratorBackup')
    .withDefaultValue(() => new DefaultValue(true).withLock())
    .withIsAvailable(scenario => {
      return (
        scenario.hasGrid === true &&
        (scenario.grid && scenario.grid.hasGrid24) === false &&
        scenario.hasGenset === true &&
        scenario.hasBattery === false
      );
    }),

  new BooleanRule('hasDifferentGeneratorDayAndNightBefore')
    .withIsAvailable(scenario => (scenario.generator && !(isMultigenset(scenario))) || false),

  new BooleanRule('hasDifferentGeneratorAfter')
    .withIsAvailable(scenario => (scenario.generator && !(isMultigenset(scenario))) || false),

  new ObjectRule<GensetExt>('generatorBeforeDay', getOneGeneratorRules('generatorBeforeDay'))
    .withDefaultValue(() => new DefaultValue(new GensetExt()))
    .withIsAvailable(scenario => (scenario.hasGenset && scenario.generator && !(isMultigenset(scenario))) || false),

  new ObjectRule<GensetExt>('generatorBeforeNight', getOneGeneratorRules('generatorBeforeNight'))
    .withDefaultValue(() => new DefaultValue(new GensetExt()))
    .withIsAvailable(scenario => {
      return !!(
        scenario.generator && !(isMultigenset(scenario)) &&
        scenario.generator.hasDifferentGeneratorDayAndNightBefore
      );
    }),

  new BooleanRule('hasDifferentGeneratorDayAndNightAfter')
    .withIsAvailable(scenario => !!(scenario.generator && !(isMultigenset(scenario)) && scenario.generator.hasDifferentGeneratorAfter) || false),

  new ObjectRule<GensetExt>('generatorAfterDay', getOneGeneratorRules('generatorAfterDay'))
    .withDefaultValue(() => new DefaultValue(new GensetExt()))
    .withIsAvailable(scenario => {
      return !!(
        scenario.generator && !(isMultigenset(scenario)) &&
        scenario.generator.hasDifferentGeneratorAfter
      );
    }),

  new ObjectRule<GensetExt>('generatorAfterNight', getOneGeneratorRules('generatorAfterNight'))
    .withDefaultValue(() => new DefaultValue(new GensetExt()))
    .withIsAvailable(scenario => {
      return !!(
        scenario.generator && !(isMultigenset(scenario)) &&
        scenario.generator.hasDifferentGeneratorAfter &&
        scenario.generator.hasDifferentGeneratorDayAndNightAfter
      );
    }),

  /* Multi only */
  new BooleanRule('isMulti')
    .withDefaultValue(() => new DefaultValue(false))
    .withIsAvailable(scenario => _env.notInProd() && scenario.hasGrid === false),

  new BooleanRule('hasDifferentTypes')
    .withIsAvailable(scenario => (scenario.generator && (isMultigenset(scenario))) || false),

  new ObjectRule<GensetExt>('generatorAfterType1', getOneGeneratorRules('generatorAfterType1'))
    .withDefaultValue(() => new DefaultValue(new GensetExt()))
    .withIsAvailable(scenario => (scenario.hasGenset && scenario.generator && (isMultigenset(scenario))) || false)
    .withCustomCheck((genset, errorMessages) => {
      const messages = errorMessages || [];
      if (genset.removeOneThreshold && genset.minimumGeneratorLoad &&
        genset.removeOneThreshold < genset.minimumGeneratorLoad) {
        messages.push(new RuleFieldMessage('scenario.generator.threshold_under_min', [
          'scenario.generator.generatorAfterType1.removeOneThreshold'
        ]));
      }
      if (genset.addOneThreshold && genset.removeOneThreshold &&
        genset.addOneThreshold < 2 * genset.removeOneThreshold) {
        messages.push(new RuleFieldMessage('scenario.generator.add_threshold_under_remove', [
          'scenario.generator.generatorAfterType1.addOneThreshold'
        ]));
      }
      return messages.length === 0;
    }),

  new ObjectRule<GensetExt>('generatorAfterType2', getOneGeneratorRules('generatorAfterType2'))
    .withDefaultValue(() => new DefaultValue(new GensetExt()))
    .withIsAvailable(scenario => (scenario.hasGenset && scenario.generator && scenario.generator.hasDifferentTypes && (isMultigenset(scenario))) || false)
    .withCustomCheck((genset, errorMessages) => {
      const messages = errorMessages || [];
      if (genset.removeOneThreshold && genset.minimumGeneratorLoad &&
        genset.removeOneThreshold < genset.minimumGeneratorLoad) {
        messages.push(new RuleFieldMessage('scenario.generator.threshold_under_min', [
          'scenario.generator.generatorAfterType2.removeOneThreshold'
        ]));
      }
      if (genset.addOneThreshold && genset.removeOneThreshold &&
        genset.addOneThreshold < 2 * genset.removeOneThreshold) {
        messages.push(new RuleFieldMessage('scenario.generator.add_threshold_under_remove', [
          'scenario.generator.generatorAfterType2.addOneThreshold'
        ]));
      }
      return messages.length === 0;
    }),

  new MinMaxRule('srLoad')
    .withIsAvailable(scenario => scenario.generator?.hasSpinningReserve() ?? false)
    .withFactor(100),

  new MinMaxRule('srPV')
    .withIsAvailable(scenario => scenario.generator?.hasSpinningReserve() ?? false)
    .withFactor(100),

  new MinMaxRule('srPower')
    .withIsAvailable(scenario => scenario.generator?.hasSpinningReserve() ?? false),

  new MinMaxRule('optimumPercentage')
    .withIsAvailable(scenario => (scenario.sizing?.algorithmRef !== SizingAlgorithmRef.algo_v3 &&
      scenario.hasBattery &&
      scenario.battery?.isRechargeableByGenerator) ?? false)
])
  .withDefaultValue(() => new DefaultValue(new ScenarioGeneratorExt()))
  .withCustomCheck((scenario, errorMessages) => {
    if (scenario.isMulti && !scenario.preSizing.done) {
      errorMessages?.push(new RuleFieldMessage(
        'scenario.generator.pre-sizing.error.mandatory',
        ['scenario.generator.preSizing']
      ));
    }
    if (scenario.isMulti && scenario.preSizing.blackout !== 0) {
      errorMessages?.push(new RuleFieldMessage(
        { key: 'scenario.generator.pre-sizing.error.blackout', params: { blackoutHours: scenario.preSizing.blackout } },
        ['scenario.generator.preSizing']
      ));
    }
    return errorMessages?.length === 0;
  })
  .withIsAvailable(scenario => scenario.hasGenset || false)
  .withFieldUsage(new FieldUsage({ preSizing: false }));

export default buildGensetRules();
